import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileWarning } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = { open: 'Open', followed_up: 'Followed up', resolved: 'Resolved' };
const STATUS_CLS = { open: 'text-rose-700', followed_up: 'text-amber-700', resolved: 'text-emerald-700' };

const SlipRow = ({ slip, headers, onSaved }) => {
  const [status, setStatus] = useState(slip.follow_up_status || 'open');
  const [note, setNote] = useState(slip.follow_up_note || '');
  const dirty = status !== (slip.follow_up_status || 'open') || note !== (slip.follow_up_note || '');

  const save = async () => {
    try {
      await axios.put(`${API}/fuel-tracker/entries/${slip.id}/follow-up`, { status, note }, { headers });
      toast.success('Follow-up saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[90px_100px_1fr_150px_1.4fr_auto] gap-2 items-center py-2 border-t text-sm" data-testid={`missing-slip-report-row-${slip.id}`}>
      <span className="text-xs text-slate-600">{slip.date}</span>
      <span className="font-semibold">{money(slip.slip_amount)}</span>
      <span className="text-xs text-slate-500 truncate">{[slip.reference, slip.litres ? `${slip.litres} L` : '', slip.note].filter(Boolean).join(' · ') || '—'}</span>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className={`h-8 ${STATUS_CLS[status]}`} data-testid={`missing-slip-status-${slip.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Follow-up note" className="h-8" data-testid={`missing-slip-followup-note-${slip.id}`} />
      <Button size="sm" variant="outline" disabled={!dirty} onClick={save} data-testid={`missing-slip-followup-save-${slip.id}`}>Save</Button>
      {slip.follow_up_by && <span className="md:col-span-6 text-[10px] text-slate-400">Last updated by {slip.follow_up_by} · {slip.follow_up_at?.slice(0, 16).replace('T', ' ')}</span>}
    </div>
  );
};

export const MissingSlipsPanel = ({ month, headers, refreshKey }) => {
  const [report, setReport] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/fuel-tracker/verification/missing-slips`, { params: { month }, headers })
      .then((r) => setReport(r.data)).catch(() => setReport(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!report || report.count === 0) return null;

  return (
    <Card className="border-rose-200" data-testid="missing-slips-panel">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><FileWarning className="h-4 w-4 text-rose-600" /> Missing Slips — {month}</CardTitle>
        <span className="text-sm text-slate-600" data-testid="missing-slips-summary">
          {report.count} slip{report.count > 1 ? 's' : ''} · <b className="text-rose-700">{money(report.total)}</b> · {report.open} still to resolve
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        {report.vehicles.map((v) => (
          <div key={v.registration} className="rounded border p-3" data-testid={`missing-slips-vehicle-${v.registration}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="font-mono font-bold">{v.registration}</span>
                <span className="text-sm text-slate-600 ml-2">{v.driver_name || 'Driver unknown'}</span>
              </div>
              <div className="text-sm">
                {v.count} slip{v.count > 1 ? 's' : ''} · <b>{money(v.total)}</b>
                {v.open > 0 ? <span className="ml-2 text-rose-700">{v.open} open</span> : <span className="ml-2 text-emerald-700">all resolved</span>}
              </div>
            </div>
            {v.slips.map((sl) => <SlipRow key={`${sl.id}-${sl.follow_up_at || ''}`} slip={sl} headers={headers} onSaved={load} />)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
